import { DEFAULT_LOCALE } from '@/constant/lang'

type GptTokenizerModule = {
  encode: (text: string) => number[]
}

let tokenizerModulePromise: Promise<GptTokenizerModule> | null = null

function getTokenizerModule() {
  if (!tokenizerModulePromise) {
    tokenizerModulePromise = import('gpt-tokenizer').then(
      (mod) => mod as unknown as GptTokenizerModule
    )
  }

  return tokenizerModulePromise
}

export function getUserAvatar() {
  const index = Math.floor(Math.random() * 20) + 1
  return `user/avatar/default/avatar${index}.png`
}

export function formatNumber(num: number, digits = 1) {
  // if (num === 0) return '0'
  // const si = [
  //   { value: 1, symbol: '' },
  //   { value: 1e3, symbol: 'k' },
  //   { value: 1e6, symbol: 'M' },
  //   { value: 1e9, symbol: 'B' },
  // ]
  // for (let i = si.length - 1; i >= 0; i--) {
  //   if (num >= si[i].value) {
  //     return (
  //       (num / si[i].value)
  //         .toFixed(digits)
  //         .replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1') + si[i].symbol
  //     )
  //   }
  // }
  // return num.toString()
  if (Number.isNaN(num)) return '0'

  if (num < 10000) {
    return Number(num.toFixed(digits)).toLocaleString()
  } else if (num < 1000000) {
    return Number((num / 1000).toFixed(digits)).toLocaleString() + 'K'
  } else {
    return Number((num / 1000000).toFixed(digits)).toLocaleString() + 'M'
  }
}

export const makeId = (n = 5) => {
  let text = ''
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

  for (let i = 0; i < n; i++)
    text += possible.charAt(Math.floor(Math.random() * possible.length))

  return text
}

// 对象数组去重
export function uniqueByKey<T>(arr: T[], identifierKey: keyof T): T[] {
  const uniqueMap = new Map<string, T>(
    arr.map((item) => [item[identifierKey] as unknown as string, item])
  )
  return Array.from(uniqueMap.values())
}

//将毫秒级时间戳转为MM-dd hh:mm:ss格式
export function formatTimestamp(timestamp: number) {
  const date = new Date(timestamp)
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const seconds = date.getSeconds().toString().padStart(2, '0')
  return `${month}-${day} ${hours}:${minutes}:${seconds}`
}

//将时间戳转化为几秒/几分...前(毫秒级时间戳)
export function formatTimestampToTimeAgo(
  timestamp: number,
  locale: string = DEFAULT_LOCALE
) {
  const now = new Date().valueOf()
  const diffInSeconds = Math.round((now - timestamp) / 1000)
  const diffInMinutes = Math.round(diffInSeconds / 60)
  const diffInHours = Math.round(diffInMinutes / 60)
  const diffInDays = Math.round(diffInHours / 24)
  const diffInMonths = Math.round(diffInDays / 30)
  const diffInYears = Math.round(diffInDays / 365)

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' })

  if (diffInSeconds < 60) {
    return rtf.format(-diffInSeconds, 'second')
  } else if (diffInMinutes < 60) {
    return rtf.format(-diffInMinutes, 'minute')
  } else if (diffInHours < 24) {
    return rtf.format(-diffInHours, 'hour')
  } else if (diffInDays < 30) {
    return rtf.format(-diffInDays, 'day')
  } else if (diffInMonths < 12) {
    return rtf.format(-diffInMonths, 'month')
  } else {
    return rtf.format(-diffInYears, 'year')
  }
}

// 格式化聊天时间，按照当地时区的 24 小时制展示
export function formatChatTimestamp(timestamp: number) {
  if (!timestamp) return ''

  const date = new Date(timestamp)
  const now = new Date()

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  const isSameYear = year === now.getFullYear()
  const isSameDay =
    isSameYear &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

  if (isSameDay) {
    return `${hours}:${minutes}`
  }

  if (isSameYear) {
    return `${month}/${day} ${hours}:${minutes}`
  }

  return `${year}/${month}/${day}`
}

//将时间戳转为3:00PM · Feb 22, 2024的格式
export function formatTimestampToAMPMTime(
  timestamp: number,
  locale: string = DEFAULT_LOCALE
) {
  const date = new Date(timestamp * 1000)

  // Format time (hours:minutes AM/PM)
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  // Format date (Month Day, Year)
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return `${timeFormatter.format(date)} · ${dateFormatter.format(date)}`
}

// 将时间戳转为 00:00 UTC, Dec 31, 2021的格式
export function formatTimestampToUTC(
  timestamp: number,
  locale: string = DEFAULT_LOCALE
) {
  if (!timestamp) return 'None'
  const date = new Date(timestamp * 1000)

  const formatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
    hour12: false,
  })

  const formattedParts = formatter.formatToParts(date)
  const values: { [key: string]: string } = {}
  formattedParts.forEach((part) => {
    values[part.type] = part.value
  })

  return `${values.hour}:${values.minute} UTC, ${values.month} ${values.day}, ${values.year}`
}
// 将时间戳转为 YYYY-MM-DD格式
export const formatDateYMD = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
// 将时间戳转为 00:00 Dec 31, 2021的格式
export function formatTimestampToLocalDate(
  timestamp?: number,
  locale: string = DEFAULT_LOCALE
) {
  if (!timestamp) return 'None'
  const date = new Date(timestamp * 1000)

  // 使用 Intl.DateTimeFormat 进行本地化格式化
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  })

  return `${timeFormatter.format(date)} ${dateFormatter.format(date)}`
}

export const getDurationNumberFormat = (durationSec: number, type: string) => {
  switch (type) {
    case 'd':
      return String(Math.floor(durationSec / 86400)).padStart(1, '0')
    case 'h':
      return String(Math.floor((durationSec % 86400) / 3600)).padStart(2, '0')
    case 'm':
      return String(Math.floor((durationSec % 3600) / 60)).padStart(2, '0')
    case 's':
      return String(Math.floor(durationSec % 60)).padStart(2, '0')
    default:
      return String(Math.floor(durationSec % 60)).padStart(2, '0')
  }
}
export const formatTimestampBracketYMDHMS = (timestamp: number) => {
  const date = new Date(timestamp * 1000)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `[${year}-${month}-${day} ${hours}:${minutes}:${seconds}]`
}

export const uuid = () => {
  return crypto?.randomUUID?.() ?? uuidv4()
}

function uuidv4() {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16)
  )
}

export function formatLetterTime(date: number) {
  const now = new Date()
  const targetDate = new Date(date)

  // 判断是否是同一天
  const isSameDay =
    targetDate.getDate() === now.getDate() &&
    targetDate.getMonth() === now.getMonth() &&
    targetDate.getFullYear() === now.getFullYear()

  // 判断是否是同一年
  const isSameYear = targetDate.getFullYear() === now.getFullYear()

  if (isSameDay) {
    // 当天显示 hh:mm
    return targetDate.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
    })
  } else if (isSameYear) {
    // 当年显示 MM/dd
    return targetDate
      .toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
      })
      .replace(/\//g, '/')
  } else {
    // 其他情况显示 yyyy/MM/dd
    return targetDate
      .toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      .replace(/\//g, '/')
  }
}

export const ms = (y: number, m: number, d: number, h = 0, min = 0, s = 0) =>
  Date.UTC(y, m - 1, d, h, min, s)

export async function estimateTokenCount(text: string): Promise<number> {
  if (!text) return 0

  const { encode } = await getTokenizerModule()
  return encode(text).length
}
